import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import type { CategoryPhotosGroup, ContainerPhotoCategory, ContainerPhotoItem } from '../shared/types/containerPhoto';
import {
  CONTAINER_PHOTO_CATEGORIES,
  getCategoryLabel,
  getCategoryShortLabel,
} from '../shared/types/containerPhoto';
import { loadContainerCategoryPhotos } from '../shared/utils/loadContainerCategoryPhotos';

const BRAND_ORANGE = '#CA4300';
const BRAND_ORANGE_HOVER = '#A03600';

interface ContainerPhotosModalProps {
  open: boolean;
  onClose: () => void;
  containerId: number;
  containerNumber: string;
  photos?: ContainerPhotoItem[];
  sealPhoto?: string | null;
  sealPhotoName?: string | null;
}

interface CategoryTab {
  category: ContainerPhotoCategory;
  label: string;
  shortLabel: string;
  images: CategoryPhotosGroup['images'];
  hasPhotos: boolean;
}

const ContainerPhotosModal: React.FC<ContainerPhotosModalProps> = ({
  open,
  onClose,
  containerId,
  containerNumber,
  photos,
  sealPhoto,
  sealPhotoName,
}) => {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<CategoryPhotosGroup[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<ContainerPhotoCategory>('seal');
  const photoIdsKey = photos?.map((photo) => photo.id).join(',') ?? '';

  const categoryTabs = useMemo<CategoryTab[]>(() => {
    const byCategory = new Map(groups.map((group) => [group.category, group.images]));
    return CONTAINER_PHOTO_CATEGORIES.map((category) => {
      const images = byCategory.get(category) ?? [];
      return {
        category,
        label: getCategoryLabel(category),
        shortLabel: getCategoryShortLabel(category),
        images,
        hasPhotos: images.length > 0,
      };
    });
  }, [groups]);

  const selectedTab = categoryTabs.find((tab) => tab.category === selectedCategory) ?? categoryTabs[0];

  useEffect(() => {
    if (!open || !containerId) {
      setGroups([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    loadContainerCategoryPhotos(containerId, { photos, sealPhoto, sealPhotoName })
      .then((loaded) => {
        if (!cancelled) setGroups(loaded);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, containerId, photoIdsKey, sealPhoto, sealPhotoName, photos]);

  useEffect(() => {
    if (!open || loading) return;
    const firstWithPhotos = categoryTabs.find((tab) => tab.hasPhotos);
    setSelectedCategory(firstWithPhotos?.category ?? 'seal');
  }, [open, loading, categoryTabs]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Container Photos - {containerNumber}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              pb: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            {categoryTabs.map((tab) => {
              const isSelected = tab.category === selectedCategory;
              const isEmpty = !tab.hasPhotos;
              return (
                <Chip
                  key={tab.category}
                  label={tab.shortLabel}
                  title={tab.label}
                  clickable={!isEmpty && !loading}
                  disabled={loading || isEmpty}
                  onClick={() => {
                    if (!isEmpty) setSelectedCategory(tab.category);
                  }}
                  variant={isSelected && !isEmpty ? 'filled' : 'outlined'}
                  sx={{
                    opacity: isEmpty ? 0.45 : 1,
                    fontWeight: isSelected && !isEmpty ? 600 : 400,
                    ...(isEmpty
                      ? {
                          color: 'text.disabled',
                          borderColor: 'action.disabled',
                          bgcolor: 'action.hover',
                        }
                      : isSelected
                        ? {
                            bgcolor: BRAND_ORANGE,
                            color: '#fff',
                            borderColor: BRAND_ORANGE,
                            '&:hover': { bgcolor: BRAND_ORANGE_HOVER },
                          }
                        : {
                            color: BRAND_ORANGE,
                            borderColor: BRAND_ORANGE,
                            '&:hover': {
                              bgcolor: 'rgba(202, 67, 0, 0.08)',
                              borderColor: BRAND_ORANGE_HOVER,
                            },
                          }),
                  }}
                />
              );
            })}
          </Box>

          {loading ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Loading photos...
            </Typography>
          ) : !selectedTab?.hasPhotos ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No photos uploaded for this category
            </Typography>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedTab.label}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: 2,
                }}
              >
                {selectedTab.images.map((img, idx) => (
                  <Box
                    key={`${selectedTab.category}-${idx}`}
                    sx={{ textAlign: 'center', minWidth: 0, overflow: 'hidden' }}
                  >
                    <img
                      src={`data:${img.contentType};base64,${img.base64}`}
                      alt={img.name}
                      style={{
                        width: '100%',
                        maxHeight: selectedTab.category === 'seal' ? 400 : 300,
                        objectFit: 'contain',
                        borderRadius: 8,
                        border: '1px solid #ddd',
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                    <Typography
                      variant="caption"
                      display="block"
                      title={img.name}
                      sx={{
                        mt: 0.5,
                        px: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {img.name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            borderColor: BRAND_ORANGE,
            color: BRAND_ORANGE,
            borderRadius: 1,
            boxShadow: 2,
            '&:hover': {
              borderColor: BRAND_ORANGE_HOVER,
              backgroundColor: 'rgba(202, 67, 0, 0.08)',
              boxShadow: 2,
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContainerPhotosModal;
